import { isDefined, isEmpty } from '@sgme/fp';
import { GreeksTable } from 'state/quotes/details/details.model';
import type { QuoteType } from 'state/quotes/infos/infos.model';
import { getBookingType } from 'state/quotes/selectors';
import { getStreamingGreeks } from 'state/quotes/streaming/streaming.selector';
import type { NextTradeAuthorizationStatus, TradeStatus } from 'state/quotes/trades/trades.model';
import { isQuoteTypeIndicative, isQuoteTypeTradable } from 'state/quotes/utils';
import { getFormRequestType, type WorkFlowStatus } from 'state/selectors';
import { getFormQuoteDetails, getFormQuoteId, getFormQuoteResult, getIsUserInternal } from 'state/selectors/common';
import type { AppState } from 'state/state.model';
import type { TradeConfirmationState } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';
import type { WorkspaceType } from 'state/workspaces/model';

export function isStatusWithTradeButtons(status: WorkFlowStatus): boolean {
  return status === 'QUOTED_TRADABLE' || status === 'QUOTED' || status === 'STREAMING_TRADABLE';
}

export function isStatusWithQuoteButtons(status: WorkFlowStatus, isIndicativeQuote: boolean): boolean {
  return (
    status === 'READY' ||
    status === 'ERROR' ||
    status === 'REJECTED' ||
    status === 'TRADE_AUTH_REJECTED' ||
    status === 'INITIALIZED' ||
    status === 'STREAM_EXPIRED' ||
    (isIndicativeQuote && status === 'QUOTED') ||
    status === 'QUOTED_PREVIOUSLY'
  );
}

export type ActionButtonsType =
  | 'None'
  | 'Quote'
  | 'QuoteAndCancel'
  | 'InitiateTrade'
  | 'ConfirmTrade'
  | 'TradeDone'
  | 'ErrorReset';

export function getSidePanelActions(
  quoteType: QuoteType | undefined,
  status: WorkFlowStatus,
  confirmation: TradeConfirmationState,
): ActionButtonsType {
  const isQuoteIndicative = quoteType !== undefined && isQuoteTypeIndicative(quoteType);
  const isQuoteTradable = quoteType !== undefined && isQuoteTypeTradable(quoteType);
  const canQuote = isStatusWithQuoteButtons(status, isQuoteIndicative);
  if (status === 'ERROR' || status === 'REJECTED') {
    return 'ErrorReset';
  }
  if (status === 'STREAM_EXPIRED') {
    return 'QuoteAndCancel';
  }
  if (canQuote) {
    return 'Quote';
  }
  if (status === 'TRADED' || status === 'STREAMING_TRADED' || status === 'BOOKED') {
    return 'TradeDone';
  }

  if (isQuoteTradable && isStatusWithTradeButtons(status)) {
    const isTrading = confirmation?.isTrading ?? false;
    const hadAlreadyTraded = confirmation?.hadTraded ?? false;

    if (isTrading) {
      return 'ConfirmTrade';
    }
    if (!hadAlreadyTraded) {
      return 'InitiateTrade';
    }
  }

  return 'None';
}

export function hasGreeksOrFeesOrMetrics(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  return hasGreeks(state, workspace, tabId) || hasFees(state, workspace, tabId) || hasMetrics(state, workspace, tabId);
}

export function hasFiccGreeks(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return !isEmpty(quoteDetails?.FICCGreeks);
}
export function getGreeks(state: AppState, workspace: WorkspaceType, tabId: string): GreeksTable | undefined {
  const requestType = getFormRequestType(state, workspace, tabId);
  if (requestType === 'RequestForStream') {
    return getStreamingGreeks(state, tabId);
  }

  return getFormQuoteDetails(state, workspace, tabId)?.Greeks;
}
export function hasGreeks(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const requestType = getFormRequestType(state, workspace, tabId);
  if (requestType === 'RequestForStream') {
    return isDefined(getStreamingGreeks(state, tabId));
  }

  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);

  return hasFiccGreeks(state, workspace, tabId) || !isEmpty(quoteDetails?.Greeks?.cells);
}

export function hasFees(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return !isEmpty(quoteDetails?.Fees);
}

export function hasMetrics(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return !isEmpty(quoteDetails?.Metrics);
}

export function hasHistoricalAverageCorrelations(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return !isEmpty(quoteDetails?.historicalAverageCorrelations?.cells);
}

export function hasPrices(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);

  return !isEmpty(quoteDetails?.prices?.cells);
}

export function hasProductGreeks(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);

  return !isEmpty(quoteDetails?.productGreeks);
}

export function hasQuoteDetails(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteDetails = getFormQuoteResult(state, workspace, tabId);
  return quoteDetails?.quoteExternalId !== undefined;
}

export function isForBooking(state: AppState, quoteId?: string): boolean {
  const bookingType = getBookingType(state, quoteId);
  return !isEmpty(bookingType);
}

export function hasProductDumpDetails(state: AppState, workspace: WorkspaceType, tabId: string): boolean {
  const quoteId = getFormQuoteId(state, workspace, tabId);
  if (isForBooking(state, quoteId)) {
    return false;
  }

  const quoteDetails = getFormQuoteDetails(state, workspace, tabId);
  return getIsUserInternal(state) && quoteDetails?.PricerRfqId !== undefined;
}

export function mapTradeStatusToWorkflowStatus(status: TradeStatus, isForBooking: boolean = false): WorkFlowStatus {
  switch (status) {
    case 'Pending':
      return isForBooking ? 'BOOKING' : 'TRADING';
    case 'Done':
      return isForBooking ? 'BOOKED' : 'TRADED';
    case 'Rejected':
      return 'REJECTED';
    default:
      return 'ERROR';
  }
}

export function mapTradeAuthorizationStatusToWorkflowStatus(
  status: NextTradeAuthorizationStatus | undefined,
): WorkFlowStatus {
  switch (status) {
    case 'Granted':
      return 'QUOTED_TRADABLE';
    case 'Failed':
    case 'DerogationRequired':
    case 'Denied':
      return 'TRADE_AUTH_REJECTED';
    case 'Pending':
      return 'QUOTED';
    case 'Timeout':
      return 'TRADE_AUTH_REJECTED';
    default:
      return 'QUOTED' as never;
  }
}
